import { checkPermissions } from "@elit-hair/elit-tools";
import {
    Button,
    Icon,
    InitialsAvatar,
    Input,
    PaginationControls,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@elit-hair/elit-ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    TbArrowDown,
    TbArrowUp,
    TbArrowsSort,
    TbChartBar,
    TbDeviceDesktopAnalytics,
    TbList,
    TbPhonePlus,
    TbSettings,
} from "react-icons/tb";
import z from "zod";

import { PAGINATION, getTotalPages } from "@/constants/pagination";
import { PERMISSIONS } from "@/constants/permissions";
import { getClarityUserImpressionsUrl } from "@/constants/urls";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { useUsers } from "@/hooks/queries/user.queries";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { cn } from "@/lib/utils";
import { useDashboardRole } from "@/pages/home/components/role-dashboard/hooks/use-dashboard-role";
import { AgentSettingsDialog } from "@/pages/management/office/components/agent-settings-dialog";
import { UserPhoneNumbersDialog } from "@/pages/management/users/components/user-phone-numbers-dialog";
import { useUserStatusContext } from "@/providers/general/user-status.provider";
import { UserStatus } from "@/types/user-statuses.types";
import type { IUser } from "@/types/user.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";
import { getStatusCircleColor } from "@/utils/user-status";

const FILTER_ALL = "all";
const HIDDEN_TEAM_NAME_PREFIX = "_";
const TABLE_COLUMN_COUNT = 5;

const SORT_COLUMNS = ["agent", "email", "team", "status"] as const;
const SORT_DIRECTIONS = ["asc", "desc"] as const;

type SortColumn = (typeof SORT_COLUMNS)[number];
type SortDirection = (typeof SORT_DIRECTIONS)[number];

type SortState = {
    column: SortColumn;
    direction: SortDirection;
};

const ManagementUsersSearchSchema = z.object({
    q: z.string().optional(),
    team: z.string().optional(),
    status: z.string().optional(),
    sort: z.enum(SORT_COLUMNS).optional(),
    dir: z.enum(SORT_DIRECTIONS).optional(),
    page: z.coerce.number().int().min(1).optional(),
});

type ManagementUsersSearch = z.infer<typeof ManagementUsersSearchSchema>;

export const Route = createFileRoute(APP_ROUTES_FULL.MANAGEMENT_USERS)({
    component: ManagementUsers,
    validateSearch: searchValidator(ManagementUsersSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "users",
                to: APP_ROUTES_TO.MANAGEMENT_USERS,
            },
            title: "users",
        },
    },
});

function isUserStatus(value: string): value is UserStatus {
    return Object.values(UserStatus).some((status) => status === value);
}

type UserTeamMembership = {
    teamIds: string[];
    teamNames: string[];
};

type UserTableRow = {
    user: IUser;
    status: UserStatus;
    teamIds: string[];
    teamNames: string[];
};

type SortableTableHeadProps = {
    label: string;
    column: SortColumn;
    sortState: SortState;
    onSort: (column: SortColumn) => void;
};

function SortableTableHead({ label, column, sortState, onSort }: SortableTableHeadProps) {
    const isActive = sortState.column === column;

    return (
        <TableHead>
            <button
                type="button"
                onClick={() => onSort(column)}
                className={cn(
                    "inline-flex items-center gap-1 rounded-sm transition-colors",
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                )}
            >
                <span>{label}</span>
                {isActive ? (
                    <Icon icon={sortState.direction === "asc" ? TbArrowUp : TbArrowDown} size={14} />
                ) : (
                    <Icon icon={TbArrowsSort} size={14} className="opacity-50" />
                )}
            </button>
        </TableHead>
    );
}

function compareRows(a: UserTableRow, b: UserTableRow, column: SortColumn): number {
    switch (column) {
        case "agent":
            return a.user.name.localeCompare(b.user.name);
        case "email":
            return a.user.email.localeCompare(b.user.email);
        case "team":
            return a.teamNames
                .slice()
                .sort((left, right) => left.localeCompare(right))
                .join(", ")
                .localeCompare(
                    b.teamNames
                        .slice()
                        .sort((left, right) => left.localeCompare(right))
                        .join(", ")
                );
        case "status":
            return a.status.localeCompare(b.status);
    }
}

function ManagementUsers() {
    const { t } = useTranslation();
    const { permissionCodes } = useDashboardRole();
    const isAdmin = checkPermissions(permissionCodes, [PERMISSIONS.CALL_SVC_ADMIN_BASE]);
    const navigate = useNavigate();
    const routeNavigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });

    const [searchInput, setSearchInput] = useState(search.q ?? "");
    const debouncedSearchInput = useDebouncedValue(searchInput);
    const [settingsUserId, setSettingsUserId] = useState<string | null>(null);
    const [phoneNumbersUser, setPhoneNumbersUser] = useState<IUser | null>(null);

    const teamFilter = search.team ?? FILTER_ALL;
    const statusFilter = search.status && isUserStatus(search.status) ? search.status : FILTER_ALL;
    const currentPage = search.page ?? PAGINATION.DEFAULT_PAGE;
    const sortState: SortState = {
        column: search.sort ?? "agent",
        direction: search.dir ?? "asc",
    };
    const isTeamFiltered = teamFilter !== FILTER_ALL;

    const { data: teamsResponse, isPending: isTeamsPending } = useTeams();
    const teams = useMemo(
        () => (teamsResponse?.data ?? []).filter((team) => !team.name.startsWith(HIDDEN_TEAM_NAME_PREFIX)),
        [teamsResponse?.data]
    );

    const { data: usersResponse, isPending: isUsersPending } = useUsers(
        {
            search: (search.q ?? "").trim() || undefined,
            page: currentPage,
            limit: PAGINATION.PAGE_SIZE,
        },
        { enabled: !isTeamFiltered }
    );

    const isInitialLoading = isTeamFiltered
        ? isTeamsPending && !teamsResponse
        : (isUsersPending && !usersResponse) || (isTeamsPending && !teamsResponse);

    const { userStatuses } = useUserStatusContext();

    useEffect(() => {
        setSearchInput(search.q ?? "");
    }, [search.q]);

    useEffect(() => {
        const nextQ = debouncedSearchInput.trim() || undefined;
        if ((search.q || undefined) === nextQ) {
            return;
        }

        routeNavigate({
            to: Route.to,
            search: (prev: ManagementUsersSearch) => ({
                ...prev,
                q: nextQ,
                page: undefined,
            }),
            replace: true,
        });
    }, [debouncedSearchInput, routeNavigate, search.q]);

    const updateSearch = (updates: Partial<ManagementUsersSearch>) => {
        routeNavigate({
            to: Route.to,
            search: (prev: ManagementUsersSearch) => ({
                ...prev,
                ...updates,
            }),
            replace: true,
        });
    };

    const teamOptions = useMemo(
        () => [...teams].map((team) => ({ id: team.id, name: team.name })).sort((a, b) => a.name.localeCompare(b.name)),
        [teams]
    );

    const membershipByUserId = useMemo(() => {
        const byUserId = new Map<string, UserTeamMembership>();

        for (const team of teams) {
            for (const member of team.members) {
                const userId = member.user?.id;
                if (!userId) {
                    continue;
                }

                const existing = byUserId.get(userId);
                if (existing) {
                    if (!existing.teamIds.includes(team.id)) {
                        existing.teamIds.push(team.id);
                        existing.teamNames.push(team.name);
                    }
                    continue;
                }

                byUserId.set(userId, {
                    teamIds: [team.id],
                    teamNames: [team.name],
                });
            }
        }

        return byUserId;
    }, [teams]);

    const { rows, totalPages } = useMemo(() => {
        const query = (search.q ?? "").trim().toLowerCase();
        const directionFactor = sortState.direction === "asc" ? 1 : -1;

        const matchesFilters = (row: UserTableRow) => {
            if (statusFilter !== FILTER_ALL && row.status !== statusFilter) {
                return false;
            }

            if (isTeamFiltered && !row.teamIds.includes(teamFilter)) {
                return false;
            }

            if (!query) {
                return true;
            }

            const name = row.user.name.toLowerCase();
            const email = row.user.email.toLowerCase();
            const teamLabel = row.teamNames.join(" ").toLowerCase();
            return name.includes(query) || email.includes(query) || teamLabel.includes(query);
        };

        const toRow = (user: IUser): UserTableRow => {
            const membership = membershipByUserId.get(user.id);
            return {
                user,
                status: userStatuses[user.id] ?? UserStatus.OFFLINE,
                teamIds: membership?.teamIds ?? [],
                teamNames: membership?.teamNames ?? [],
            };
        };

        if (isTeamFiltered) {
            const teamUsers = new Map<string, IUser>();
            for (const team of teams) {
                if (team.id !== teamFilter) {
                    continue;
                }
                for (const member of team.members) {
                    if (member.user?.id) {
                        teamUsers.set(member.user.id, member.user);
                    }
                }
            }

            const filteredRows = Array.from(teamUsers.values())
                .map(toRow)
                .filter(matchesFilters)
                .sort((a, b) => compareRows(a, b, sortState.column) * directionFactor);

            const pages = Math.max(getTotalPages(filteredRows.length, PAGINATION.PAGE_SIZE), 1);
            const pageIndex = Math.min(currentPage, pages) - 1;
            const start = pageIndex * PAGINATION.PAGE_SIZE;

            return {
                rows: filteredRows.slice(start, start + PAGINATION.PAGE_SIZE),
                totalPages: pages,
            };
        }

        const users = usersResponse?.users ?? [];
        const filteredRows = users
            .map(toRow)
            .filter(matchesFilters)
            .sort((a, b) => compareRows(a, b, sortState.column) * directionFactor);

        return {
            rows: filteredRows,
            totalPages: Math.max(getTotalPages(usersResponse?.total ?? 0, PAGINATION.PAGE_SIZE), 1),
        };
    }, [
        search.q,
        sortState,
        statusFilter,
        isTeamFiltered,
        teamFilter,
        membershipByUserId,
        userStatuses,
        teams,
        currentPage,
        usersResponse?.users,
        usersResponse?.total,
    ]);

    const handleSort = (column: SortColumn) => {
        if (sortState.column === column) {
            updateSearch({
                sort: column,
                dir: sortState.direction === "asc" ? "desc" : "asc",
                page: undefined,
            });
            return;
        }

        updateSearch({ sort: column, dir: "asc", page: undefined });
    };

    const handlePageChange = (page: number) => {
        updateSearch({
            page: page === PAGINATION.DEFAULT_PAGE ? undefined : page,
        });
    };

    const handleGoToCalls = (userId: string) => {
        navigate({
            to: APP_ROUTES_TO.CALLS,
            search: (old) => ({ ...old, agent: userId }),
        });
    };

    const handleGoToKpis = (userId: string) => {
        navigate({
            to: APP_ROUTES_TO.PERFORMANCE_KPIS,
            search: { agent: userId },
        });
    };

    return (
        <div className="flex h-full min-h-0 flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
                <Input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder={t("search_users")}
                    className="max-w-sm"
                />
                <Select
                    value={teamFilter}
                    onValueChange={(value) =>
                        updateSearch({
                            team: value === FILTER_ALL ? undefined : value,
                            page: undefined,
                        })
                    }
                >
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder={t("team")} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={FILTER_ALL}>{t("all")}</SelectItem>
                        {teamOptions.map((team) => (
                            <SelectItem key={team.id} value={team.id}>
                                {team.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={statusFilter}
                    onValueChange={(value) =>
                        updateSearch({
                            status: value === FILTER_ALL ? undefined : value,
                            page: undefined,
                        })
                    }
                >
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder={t("status")} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={FILTER_ALL}>{t("all")}</SelectItem>
                        {Object.values(UserStatus).map((status) => (
                            <SelectItem key={status} value={status}>
                                {t(`user_status.${status}`)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <SortableTableHead
                                label={t("agent")}
                                column="agent"
                                sortState={sortState}
                                onSort={handleSort}
                            />
                            <SortableTableHead
                                label={t("email")}
                                column="email"
                                sortState={sortState}
                                onSort={handleSort}
                            />
                            <SortableTableHead
                                label={t("team")}
                                column="team"
                                sortState={sortState}
                                onSort={handleSort}
                            />
                            <SortableTableHead
                                label={t("status")}
                                column="status"
                                sortState={sortState}
                                onSort={handleSort}
                            />
                            <TableHead className="text-right">{t("actions")}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isInitialLoading ? (
                            <TableRow>
                                <td colSpan={TABLE_COLUMN_COUNT} className="h-32 p-2 text-center align-middle">
                                    <div className="flex justify-center py-8">
                                        <Spinner size="sm" />
                                    </div>
                                </td>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <td
                                    colSpan={TABLE_COLUMN_COUNT}
                                    className="h-32 p-2 text-center align-middle text-muted-foreground"
                                >
                                    {t("no_users_found")}
                                </td>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.user.id}>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <InitialsAvatar name={row.user.name} size="sm" />
                                            <span className="font-medium">{row.user.name}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">{row.user.email}</TableCell>
                                    <TableCell>
                                        <span className="text-sm">
                                            {[...row.teamNames].sort((a, b) => a.localeCompare(b)).join(", ")}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${getStatusCircleColor(row.status)}`}
                                            />
                                            <span className="text-sm capitalize">{t(`user_status.${row.status}`)}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center justify-end gap-1">
                                            {isAdmin && row.user.id ? (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            asChild
                                                            variant="ghost"
                                                            size="icon"
                                                            aria-label={t("open_clarity_recordings")}
                                                        >
                                                            <a
                                                                href={getClarityUserImpressionsUrl(row.user.id)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <Icon icon={TbDeviceDesktopAnalytics} />
                                                            </a>
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t("open_clarity_recordings")}</TooltipContent>
                                                </Tooltip>
                                            ) : null}
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        aria-label={t("go_to_calls")}
                                                        onClick={() => handleGoToCalls(row.user.id)}
                                                    >
                                                        <Icon icon={TbList} />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>{t("go_to_calls")}</TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        aria-label={t("go_to_kpis")}
                                                        onClick={() => handleGoToKpis(row.user.id)}
                                                    >
                                                        <Icon icon={TbChartBar} />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>{t("go_to_kpis")}</TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        aria-label={t("add_phone_number")}
                                                        onClick={() => setPhoneNumbersUser(row.user)}
                                                    >
                                                        <Icon icon={TbPhonePlus} />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>{t("add_phone_number")}</TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        aria-label={t("open_user_settings")}
                                                        onClick={() => setSettingsUserId(row.user.id)}
                                                    >
                                                        <Icon icon={TbSettings} />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>{t("open_user_settings")}</TooltipContent>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <PaginationControls
                currentPage={Math.min(currentPage, totalPages)}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={isInitialLoading}
                className="shrink-0"
            />

            <AgentSettingsDialog
                open={settingsUserId !== null}
                onOpenChange={(open) => {
                    if (!open) setSettingsUserId(null);
                }}
                userId={settingsUserId ?? undefined}
            />

            <UserPhoneNumbersDialog
                open={phoneNumbersUser !== null}
                onOpenChange={(open) => {
                    if (!open) setPhoneNumbersUser(null);
                }}
                user={phoneNumbersUser}
            />
        </div>
    );
}
